import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate, useOutletContext } from 'react-router-dom';
import {
  Search,
  Filter,
  Eye,
  Pencil,
  Trash2,
  Loader2,
  Calendar,
  ChevronDown,
  X,
  Inbox,
  AlertCircle,
  Clock,
  CheckCircle,
  XCircle,
  FileText,
} from 'lucide-react';

import ReviewModal from './ReviewModal';
import { fetchNewsForDashboard, updateNewsStatus, deleteNews } from '../../utils/api/newsApi';

const content = {
  ne: {
    pageTitle: 'सबै लेखहरू',
    search: 'शीर्षक वा लेखकद्वारा खोज्नुहोस्...',
    status: 'स्थिति',
    sector: 'क्षेत्र',
    all: 'सबै',
    pending: 'समीक्षा बाँकी',
    published: 'प्रकाशित',
    rejected: 'अस्वीकृत',
    noResults: 'कुनै लेख भेटिएन',
    noResultsSub: 'फिल्टर परिवर्तन गरेर पुनः प्रयास गर्नुहोस्',
    retry: 'फेरि प्रयास गर्नुहोस्',
    clearFilters: 'फिल्टर हटाउनुहोस्',
    title: 'शीर्षक',
    reporter: 'रिपोर्टर',
    date: 'मिति',
    actions: 'कार्य',
    review: 'समीक्षा',
    edit: 'सम्पादन',
    delete: 'मेटाउनुहोस्',
    showing: 'देखाउँदै',
    articles: 'लेखहरू',
  },
  en: {
    pageTitle: 'All Articles',
    search: 'Search by title or author...',
    status: 'Status',
    sector: 'Sector',
    all: 'All',
    pending: 'Pending',
    published: 'Published',
    rejected: 'Rejected',
    noResults: 'No articles found',
    noResultsSub: 'Try adjusting your filters or search term',
    retry: 'Retry',
    clearFilters: 'Clear filters',
    title: 'Title',
    reporter: 'Reporter',
    date: 'Date',
    actions: 'Actions',
    review: 'Review',
    edit: 'Edit',
    delete: 'Delete',
    showing: 'Showing',
    articles: 'articles',
  },
};

const statusConfig = {
  pending:   { label: 'Pending',   labelNe: 'समीक्षा बाँकी', icon: Clock,        badge: 'bg-amber-50 text-amber-600 border-amber-200' },
  published: { label: 'Published', labelNe: 'प्रकाशित',      icon: CheckCircle,  badge: 'bg-primary-50 text-primary border-primary-200' },
  rejected:  { label: 'Rejected',  labelNe: 'अस्वीकृत',      icon: XCircle,      badge: 'bg-red-50 text-red-600 border-red-200' },
};

const TAB_STATUSES = ['all', 'pending', 'published', 'rejected'];

const relativeDate = (dateStr) => {
  if (!dateStr) return '—';
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const hrs = Math.floor(diffMs / 3600000);
  if (hrs < 1) return 'just now';
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
};

const initials = (name) =>
  (name || '?').split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('');

const SkeletonRow = () => (
  <tr className="border-b border-char-50">
    {[...Array(5)].map((_, i) => (
      <td key={i} className="px-5 py-4">
        <div className="h-4 animate-pulse rounded bg-char-100" style={{ width: `${60 + i * 10}%` }} />
      </td>
    ))}
  </tr>
);

const AllArticles = () => {
  const { language = "ne" } = useOutletContext() || {};
  const t = content[language] || content.en;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeArticle, setActiveArticle] = useState(null);
  const [actionLoadingId, setActionLoadingId] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [sectorFilter, setSectorFilter] = useState('all');
  const [sectorOpen, setSectorOpen] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const statuses = ['pending', 'published', 'rejected'];
      const results = await Promise.all(
        statuses.map((s) => fetchNewsForDashboard({ status: s, page: 1, limit: 100 }))
      );
      const merged = results.flatMap((r, i) =>
        (r?.data ?? []).map((a) => ({ ...a, status: statuses[i] }))
      );
      setArticles(merged);
    } catch (err) {
      setError(err?.message || 'Failed to load articles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Keep URL in sync with status filter
  useEffect(() => {
    if (statusFilter !== 'all') setSearchParams({ status: statusFilter });
    else setSearchParams({});
  }, [statusFilter]);

  const SECTORS = [
    'शहरी योजना तथा भौतिक पूर्वाधार',
    'सुरक्षा तथा सार्वजनिक सुरक्षा',
    'खानेपानी तथा जल व्यवस्थापन',
    'फोहोर तथा प्रदूषण व्यवस्थापन',
    'वन, वातावरण तथा पर्यटन',
    'शिक्षा, अनुसन्धान तथा ज्ञान विकास',
    'शिल्प, सीप तथा उद्यमशीलता विकास',
    'स्वास्थ्य तथा जनस्वास्थ्य',
    'स्वस्थ जीवनशैली तथा उपभोक्ता हित संरक्षण',
    'खेलकुद तथा खेल प्रशिक्षण',
    'सूचना प्रविधि तथा स्मार्ट सिटी विकास',
    'मिडिया तथा सञ्चार',
    'व्यापार, उद्योग तथा आर्थिक विकास',
    'कृषि तथा सहकारी विकास',
    'विपद् व्यवस्थापन तथा जलवायु अनुकूलन',
    'कला, संस्कृति तथा पहिचान संरक्षण',
    'सुशासन, आर्थिक नीति तथा वित्तीय व्यवस्थापन',
    'सामाजिक सुरक्षा तथा समावेशी विकास',
    'महिला, बालबालिका तथा युवा विकास',
    'अन्तर्राष्ट्रिय सम्बन्ध तथा साझेदारी',
    'प्रशासन तथा संस्थागत व्यवस्थापन',
  ];

  const sectors = useMemo(() => ['all', ...SECTORS], []);

  const filtered = useMemo(() => {
    return articles.filter((a) => {
      const matchStatus = statusFilter === 'all' || a.status === statusFilter;
      const matchSector = sectorFilter === 'all' || a.sector === sectorFilter;
      const q = searchQuery.toLowerCase();
      const matchSearch =
        !q ||
        a.title?.toLowerCase().includes(q) ||
        a.author?.name?.toLowerCase().includes(q);
      return matchStatus && matchSector && matchSearch;
    });
  }, [articles, statusFilter, sectorFilter, searchQuery]);

  const counts = useMemo(() => {
    const c = { all: articles.length, pending: 0, published: 0, rejected: 0 };
    articles.forEach((a) => { if (c[a.status] !== undefined) c[a.status]++; });
    return c;
  }, [articles]);

  const handleApprove = async (articleId) => {
    setActionLoadingId(articleId);
    try {
      await updateNewsStatus(articleId, 'published');
      setArticles((prev) => prev.map((a) => (a._id ?? a.id) === articleId ? { ...a, status: 'published' } : a));
      setActiveArticle(null);
    } catch (err) { setError(err?.message || 'Failed to publish'); }
    finally { setActionLoadingId(null); }
  };

  const handleReject = async (articleId, remarks) => {
    if (!remarks?.trim()) { setError('Remarks required'); return; }
    setActionLoadingId(articleId);
    try {
      await updateNewsStatus(articleId, 'rejected', remarks);
      setArticles((prev) => prev.map((a) => (a._id ?? a.id) === articleId ? { ...a, status: 'rejected' } : a));
      setActiveArticle(null);
    } catch (err) { setError(err?.message || 'Failed to reject'); }
    finally { setActionLoadingId(null); }
  };

  const handleDelete = async (articleId) => {
    setActionLoadingId(articleId);
    try {
      await deleteNews(articleId);
      setArticles((prev) => prev.filter((a) => (a._id ?? a.id) !== articleId));
      setActiveArticle(null);
    } catch (err) { setError(err?.message || 'Failed to delete'); }
    finally { setActionLoadingId(null); }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setSectorFilter('all');
  };

  const hasFilters = searchQuery || statusFilter !== 'all' || sectorFilter !== 'all';

  return (
    <div className="w-full space-y-6 overflow-x-hidden">
      {/* Page header */}
      <div>
        <h2 className="text-2xl font-extrabold text-char-900">{t.pageTitle}</h2>
        <p className="mt-0.5 text-sm text-char-400">
          {t.showing} <span className="font-bold text-char-700">{filtered.length}</span> {t.articles}
        </p>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-600">
          <AlertCircle size={18} className="mt-0.5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={fetchAll} className="flex-shrink-0 rounded-lg px-2 py-0.5 underline underline-offset-2 hover:bg-red-100">
            {t.retry}
          </button>
        </div>
      )}

      {/* Status tabs */}
      <div className="flex gap-1 overflow-x-auto rounded-2xl border border-char-100 bg-white p-1 shadow-sm">
        {TAB_STATUSES.map((s) => {
          const cfg = statusConfig[s];
          const isActive = statusFilter === s;
          return (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`flex flex-shrink-0 items-center gap-2 rounded-xl px-4 py-2 text-sm font-bold transition-all ${
                isActive
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-char-500 hover:bg-char-50 hover:text-char-700'
              }`}
            >
              {cfg && <cfg.icon size={14} />}
              {!cfg && <FileText size={14} />}
              <span>{s === 'all' ? t.all : (language === 'ne' ? cfg.labelNe : cfg.label)}</span>
              <span className={`rounded-full px-1.5 py-0.5 text-[11px] font-extrabold ${
                isActive ? 'bg-white/20 text-white' : 'bg-char-100 text-char-500'
              }`}>
                {counts[s]}
              </span>
            </button>
          );
        })}
      </div>

      {/* Search + sector filter row */}
      <div className="flex flex-col gap-3 sm:flex-row">
        {/* Search */}
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-char-300" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t.search}
            className="w-full rounded-2xl border border-char-200 bg-white py-2.5 pl-10 pr-9 text-sm text-char-800 placeholder-char-300 transition-colors focus:border-primary focus:outline-none"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-char-300 hover:text-char-500">
              <X size={14} />
            </button>
          )}
        </div>

        {/* Sector dropdown */}
        <div className="relative" onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setSectorOpen(false); }}>
          <button
            onClick={() => setSectorOpen((o) => !o)}
            className="flex items-center gap-2 rounded-2xl border border-char-200 bg-white px-4 py-2.5 text-sm font-bold text-char-600 transition-colors hover:border-char-300"
          >
            <Filter size={14} />
            <span>{sectorFilter === 'all' ? t.sector : sectorFilter}</span>
            <ChevronDown size={14} className={`transition-transform ${sectorOpen ? 'rotate-180' : ''}`} />
          </button>
          {sectorOpen && (
            <div className="absolute right-0 z-20 mt-1.5 min-w-[160px] rounded-2xl border border-char-100 bg-white py-1.5 shadow-xl">
              {sectors.map((sec) => (
                <button
                  key={sec}
                  tabIndex={0}
                  onClick={() => { setSectorFilter(sec); setSectorOpen(false); }}
                  className={`flex w-full items-center gap-2 px-4 py-2 text-sm transition-colors ${
                    sectorFilter === sec
                      ? 'bg-primary-50 font-bold text-primary'
                      : 'text-char-600 hover:bg-char-50'
                  }`}
                >
                  {sec === 'all' ? t.all : sec}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Clear filters */}
        {hasFilters && (
          <button
            onClick={clearFilters}
            className="flex items-center gap-1.5 rounded-2xl border border-char-200 bg-white px-4 py-2.5 text-sm font-bold text-char-500 transition-colors hover:border-char-300 hover:text-char-700"
          >
            <X size={14} />
            {t.clearFilters}
          </button>
        )}
      </div>

      {/* Table — desktop */}
      <div className="hidden overflow-hidden rounded-3xl border border-char-100 bg-white shadow-sm sm:block">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-char-100 bg-char-50/60 text-left text-xs font-extrabold uppercase tracking-wider text-char-400">
                <th className="px-5 py-3.5">{t.title}</th>
                <th className="px-5 py-3.5">{t.status}</th>
                <th className="px-5 py-3.5">{t.reporter}</th>
                <th className="px-5 py-3.5">{t.date}</th>
                <th className="px-5 py-3.5 text-right">{t.actions}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(6)].map((_, i) => <SkeletonRow key={i} />)
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={5}>
                    <div className="flex flex-col items-center gap-2 py-16">
                      <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-char-50 text-char-300">
                        <Inbox size={22} />
                      </span>
                      <p className="text-sm font-bold text-char-500">{t.noResults}</p>
                      <p className="text-xs text-char-400">{t.noResultsSub}</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filtered.map((article) => {
                  const id = article._id ?? article.id;
                  const isLoading = actionLoadingId === id;
                  const cfg = statusConfig[article.status] || statusConfig.pending;

                  return (
                    <tr key={id} className="border-b border-char-50 transition-colors last:border-0 hover:bg-char-50/40">
                      <td className="max-w-xs px-5 py-3.5 font-bold text-char-900">
                        <span className="line-clamp-1">{article.title}</span>
                      </td>
                      <td className="px-5 py-3.5">
                        <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-bold ${cfg.badge}`}>
                          <cfg.icon size={11} />
                          {language === 'ne' ? cfg.labelNe : cfg.label}
                        </span>
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-2">
                          <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-primary-50 text-[11px] font-extrabold text-primary">
                            {initials(article.author?.name)}
                          </span>
                          <span className="text-char-500">{article.author?.name || '—'}</span>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-5 py-3.5 text-char-400">
                        {relativeDate(article.createdAt)}
                      </td>
                      <td className="px-5 py-3.5">
                        {isLoading ? (
                          <div className="flex justify-end px-2">
                            <Loader2 size={16} className="animate-spin text-primary" />
                          </div>
                        ) : (
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => setActiveArticle(article)}
                              title={t.review}
                              className="rounded-xl p-2 text-char-400 transition-colors hover:bg-primary-50 hover:text-primary"
                            >
                              <Eye size={16} />
                            </button>
                            <button
                              onClick={() => navigate('/editor/create-article', { state: { article } })}
                              title={t.edit}
                              className="rounded-xl p-2 text-char-400 transition-colors hover:bg-blue-50 hover:text-blue-600"
                            >
                              <Pencil size={16} />
                            </button>
                            <button
                              onClick={() => setActiveArticle({ ...article, mode: 'delete' })}
                              title={t.delete}
                              className="rounded-xl p-2 text-char-400 transition-colors hover:bg-red-50 hover:text-red-600"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile cards */}
      <div className="space-y-3 sm:hidden">
        {loading ? (
          [...Array(4)].map((_, i) => (
            <div key={i} className="animate-pulse rounded-2xl border border-char-100 bg-white p-4">
              <div className="h-4 w-3/4 rounded bg-char-100" />
              <div className="mt-3 h-3 w-1/2 rounded bg-char-100" />
            </div>
          ))
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center gap-2 rounded-3xl border border-dashed border-char-200 bg-white py-12">
            <Inbox size={22} className="text-char-300" />
            <p className="text-sm font-bold text-char-500">{t.noResults}</p>
          </div>
        ) : (
          filtered.map((article) => {
            const id = article._id ?? article.id;
            const isLoading = actionLoadingId === id;
            const cfg = statusConfig[article.status] || statusConfig.pending;

            return (
              <div key={id} className="rounded-2xl border border-char-100 bg-white p-4">
                <div className="flex items-start justify-between gap-3">
                  <p className="line-clamp-2 flex-1 text-sm font-bold text-char-900">{article.title}</p>
                  <span className={`flex-shrink-0 rounded-full border px-2 py-0.5 text-xs font-bold ${cfg.badge}`}>
                    {language === 'ne' ? cfg.labelNe : cfg.label}
                  </span>
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-50 text-[10px] font-extrabold text-primary">
                      {initials(article.author?.name)}
                    </span>
                    <span className="text-xs text-char-500">{article.author?.name || '—'}</span>
                  </div>
                  <span className="flex items-center gap-1 text-xs text-char-400">
                    <Calendar size={12} />
                    {relativeDate(article.createdAt)}
                  </span>
                </div>
                <div className="mt-3 flex justify-end gap-1 border-t border-char-50 pt-2">
                  {isLoading ? (
                    <Loader2 size={16} className="animate-spin text-primary" />
                  ) : (
                    <>
                      <button onClick={() => setActiveArticle(article)} className="rounded-xl p-2 text-char-400 hover:bg-primary-50 hover:text-primary">
                        <Eye size={16} />
                      </button>
                      <button onClick={() => navigate('/editor/create-article', { state: { article } })} className="rounded-xl p-2 text-char-400 hover:bg-blue-50 hover:text-blue-600">
                        <Pencil size={16} />
                      </button>
                      <button onClick={() => setActiveArticle({ ...article, mode: 'delete' })} className="rounded-xl p-2 text-char-400 hover:bg-red-50 hover:text-red-600">
                        <Trash2 size={16} />
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {activeArticle && (
        <ReviewModal
          article={activeArticle}
          language={language}
          loading={actionLoadingId === (activeArticle._id ?? activeArticle.id)}
          onApprove={() => handleApprove(activeArticle._id ?? activeArticle.id)}
          onReject={(remarks) => handleReject(activeArticle._id ?? activeArticle.id, remarks)}
          onDelete={activeArticle.mode === 'delete' ? () => handleDelete(activeArticle._id ?? activeArticle.id) : undefined}
          onClose={() => setActiveArticle(null)}
        />
      )}
    </div>
  );
};

export default AllArticles;